import { Add, AddTask, Delete, Edit, SearchOutlined } from "@mui/icons-material";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, InputBase, Tooltip, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useState, useRef, useEffect, useMemo } from "react";
import { DeleteTaskMapping, GetProviderList, GetAllCustomFields, GetAllCRMTags, GetTaskMappings, GetOrderEntities, GetAllCerboTags, DeleteOrderMapping } from "../../Service/AxiosService";
import AddOrders from "./AddOpenOrders";
import { Checkbox } from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const ROW_HEIGHT = 37;

const OpenOrderList = () => {
    const [open, setOpen] = useState(false);
    const [openOrders, setOpenOrders] = useState<any[]>([]);
    const [crmTags, setCrmTags] = useState<any[]>([]);
    const [tagList, setTagList] = useState<any[]>([]);
    const [customFieldList, setCustomFieldList] = useState<any[]>([]);
    const [editRow, setEditRow] = useState<any | null>(null);
    const [deleteRow, setDeleteRow] = useState<any | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const columns: GridColDef[] = [
        { field: 'sNo', headerName: 'S.No', flex: 0.5, minWidth: 60, headerAlign: 'center', align: 'center' },
        { field: 'Ordername', headerName: 'Order Name', flex: 0.85, minWidth: 130, headerAlign: 'left', align: 'left' },
        { field: 'cerboTags', headerName: 'Cerbo Tags', flex: 0.95, minWidth: 120, headerAlign: 'left', align: 'left',
            renderCell: (params) => {
                const tagString = params.value || '';
                if (!tagString) return null;
                return (
                    <Tooltip title={tagString} placement="top" arrow>
                        <span
                            style={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: 'block',
                                width: '100%',
                            }}
                        >
                            {tagString}
                        </span>
                    </Tooltip>
                );
            },
        },
        { field: 'GHLTagName', headerName: 'HL Tag', flex: 0.8, minWidth: 100, headerAlign: 'left', align: 'left' },
        { field: 'CustomFieldId', headerName: 'Custom Field', flex: 0.8, minWidth: 100, headerAlign: 'left', align: 'left',
            renderCell: (params) => {
                const fieldId = params.value || '';
                if (!fieldId) return null;
                const fieldName = customFieldList.find((f: any) => f.id === fieldId)?.name || fieldId;
                return (
                    <Tooltip title={fieldName} placement="top" arrow>
                        <span
                            style={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: 'block',
                                width: '100%',
                            }}
                        >
                            {fieldName}
                        </span>
                    </Tooltip>
                );
            },
        },
        { field: 'CustomFieldValue', headerName: 'Custom Field Value', flex: 0.8, minWidth: 110, headerAlign: 'left', align: 'left' },
        { field: 'SelectedTagId', headerName: 'Cerbo Notes Tag', flex: 0.8, minWidth: 110, headerAlign: 'left', align: 'left',
            renderCell: (params) => {
                const tagId = params.value || '';
                if (!tagId) return null;
                const tagName = tagList.find((t: any) => t.id === tagId)?.name || tagId;
                return (
                    <Tooltip title={tagName} placement="top" arrow>
                        <span
                            style={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: 'block',
                                width: '100%',
                            }}
                        >
                            {tagName}
                        </span>
                    </Tooltip>
                );
            },
        },
        { field: 'TagNotes', headerName: 'Tag Notes', flex: 0.95, minWidth: 120, headerAlign: 'left', align: 'left',
            renderCell: (params) => {
                const notes = params.value || '';
                if (!notes) return null;
                return (
                    <Tooltip title={notes} placement="top" arrow>
                        <span
                            style={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: 'block',
                                width: '100%',
                            }}
                        >
                            {notes}
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 0.65,
            minWidth: 90,
            headerAlign: 'center',
            align: 'center',
            sortable: false,
            filterable: false,
            disableReorder: true,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                    <IconButton
                        aria-label="edit"
                        color="primary"
                        size="small"
                        onClick={() => handleEdit(params.row)}
                    >
                        <Edit sx={{ fontSize: 15 }} />
                    </IconButton>
                    <IconButton
                        aria-label="delete"
                        color="error"
                        size="small"
                        onClick={() => handleDelete(params.row)}
                    >
                        <Delete sx={{ fontSize: 15 }} />
                    </IconButton>
                </Box>
            ),
        },
    ];

    const dataGridWrapperRef = useRef<HTMLDivElement>(null);
    // Dynamic page size calculation
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

    const calculatePageSize = (gridEl: HTMLDivElement) => {
        const header = gridEl.querySelector('.MuiDataGrid-columnHeaders');
        const footer = gridEl.querySelector('.MuiDataGrid-footerContainer');

        const headerHeight = header?.getBoundingClientRect().height ?? 0;
        const footerHeight = footer?.getBoundingClientRect().height ?? 0;
        const gridHeight = gridEl.getBoundingClientRect().height;

        const availableHeight = gridHeight - headerHeight - footerHeight;
        const rowsThatFit = Math.floor(availableHeight / ROW_HEIGHT);

        return Math.max(5, rowsThatFit);
    };

    const handleEdit = (row: any) => {
        setEditRow(row);
        setOpen(true);
    };

    const handleDelete = (row: any) => {
        setDeleteRow(row);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (deleteRow?.id) {
            await DeleteOrderMapping(deleteRow.id);
            await TaskMapping();
        }
        setDeleteDialogOpen(false);
        setDeleteRow(null);
    };

    const handleOnClose = async () => {
        setOpen(false);
        setEditRow(null);
        await TaskMapping();
    }


    useEffect(() => {
        document.title = `${process.env.REACT_APP_CLINIC_NAME || 'Clinic'} - Open Orders`;

        if (!dataGridWrapperRef.current) return;
        const observer = new ResizeObserver(() => {
            const newPageSize = calculatePageSize(dataGridWrapperRef.current!);
            setPaginationModel(prev => {
                if (prev.pageSize === newPageSize) return prev;
                return { ...prev, pageSize: newPageSize, page: 0 };
            });
        });
        observer.observe(dataGridWrapperRef.current);
        return () => observer.disconnect();
    }, []);
    const pageSizeOptions = useMemo(() => [5, 10, 20, 50, paginationModel.pageSize].filter((v, i, arr) => arr.indexOf(v) === i).sort((a, b) => a - b), [paginationModel.pageSize]);

    const filteredOrders = useMemo(() => {
        if (!searchTerm.trim()) return openOrders;
        const term = searchTerm.toLowerCase();
        return openOrders.filter((row) =>
            row.Ordername?.toLowerCase().includes(term) ||
            row.cerboTags?.toLowerCase().includes(term) ||
            row.GHLTagName?.toLowerCase().includes(term) ||
            row.CustomFieldValue?.toLowerCase().includes(term) ||
            row.TagNotes?.toLowerCase().includes(term)
        );
    }, [openOrders, searchTerm]);

    useEffect(() => {
        TaskMapping();
    }, []);

    const TaskMapping = async () => {
        
        const cerboTagIds = await GetAllCerboTags();
        const rawCerbo = cerboTagIds?.data ?? cerboTagIds;
        const cerboData: any[] = Array.isArray(rawCerbo)
            ? rawCerbo
            : Array.isArray(rawCerbo?.data)
            ? rawCerbo.data
            : [];

            const formattedTags = cerboData.map((tag: any) => ({
                id: tag.id,
                name: `${tag.tagCategory} - ${tag.name}`,
            }));
            setTagList(formattedTags);
            // Empty tag list is normal for a new clinic — only alert on a real fetch failure.
            if (cerboTagIds && cerboTagIds.success === false) {
                alert("Failed to fetch HL tags: " + (cerboTagIds.error ?? 'Unknown error'));
            }

        const crmTags = await GetAllCRMTags();
        const rawCrm = crmTags?.data ?? crmTags;
        const crmTagsData: any[] = Array.isArray(rawCrm)
            ? rawCrm
            : Array.isArray(rawCrm?.data)
            ? rawCrm.data
            : [];

        const formattedCrmTags = crmTagsData.length > 0
            ? crmTagsData.map((tag: any) => ({ id: tag.tagId, name: tag.tagName }))
            : [];

        setCrmTags(formattedCrmTags);

        const customFields = await GetAllCustomFields();
        const rawCustomFields = customFields?.data ?? customFields;
        const customFieldsData: any[] = Array.isArray(rawCustomFields)
            ? rawCustomFields
            : Array.isArray(rawCustomFields?.data)
            ? rawCustomFields.data
            : [];

        const formattedCustomFields = customFieldsData.length > 0
            ? customFieldsData.map((field: any) => ({ id: field.id, name: field.fieldName || field.name }))
            : [];

        setCustomFieldList(formattedCustomFields);

        const res = await GetOrderEntities();
        if (res.success && res.data) {
            const data = Array.isArray(res.data) ? res.data : (res.data.data || res.data.items || []);
            const formattedData = data.map((item: any, index: number) => ({
                id: item.id,
                sNo: index + 1,
                cerboTags: cerboData.filter((tag: any) => (item.cerboTags?.split(',') || []).includes(tag.id)).map((t: any) => `${t.tagCategory} - ${t.name}`).join(", "),
                GHLTagName: formattedCrmTags.find((a: any) => a.id === item.crmTag)?.name || '',
                Ordername: item.ordername,
                CustomFieldId: item.customFieldId || '',
                CustomFieldValue: item.customFieldValue || '',
                SelectedTagId: item.selectedTagId || '',
                TagNotes: item.tagNotes || '',
            }));
            setOpenOrders(formattedData);
        } else {
            alert("Failed to fetch Open Orders mappings: " + res.error);
        }
    };

    return (
        <Box sx={{ width: '100%', height: '100%', p: 0, m: 0, bgcolor: 'transparent', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flexShrink: 0, zIndex: 1 }}>
                <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 1, mt: 0.5 }}>
                    <Grid>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: 17,
                            }}
                            variant="subtitle1"
                        >
                            Open Orders
                        </Typography>
                    </Grid>
                    <Grid>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: 300,
                                    bgcolor: '#fff',
                                    height: 36,
                                    border: '1px solid #dedede',
                                    borderRadius: 2,
                                }}
                            >
                                <InputBase
                                    placeholder="Search Open Orders..."
                                    inputProps={{ 'aria-label': 'search Open Orders' }}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    sx={{
                                        flex: 1,
                                        fontSize: 15,
                                        bgcolor: 'transparent',
                                        px: 2,
                                        py: 0.5,
                                        height: 32,
                                    }}
                                />
                                <IconButton sx={{ p: '4px', color: '#23244A', height: 32, width: 32 }} aria-label="search" size="small">
                                    <SearchOutlined fontSize="small" />
                                </IconButton>
                            </Box>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<Add fontSize="small" />}
                                sx={{
                                    textTransform: 'none',
                                    boxShadow: 'none',
                                    bgcolor: '#3D52A0',
                                    '&:hover': { bgcolor: '#31417f' },
                                    fontSize: 14,
                                    borderRadius: 2,
                                    minWidth: 120,
                                    py: 1,
                                    px: 2,
                                    height: 36,
                                }}
                                onClick={() => setOpen(true)}
                            >
                                New Order Rule
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
            <Box ref={dataGridWrapperRef}
                sx={{
                    flex: 1,
                    width: '100%',
                    minHeight: 0,
                    pb: 0,
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: '#fff',
                    borderRadius: 3,
                    boxShadow: '0 2px 16px rgba(24,26,42,0.08)',
                }}
            >
                <DataGrid
                    rows={filteredOrders}
                    columns={columns}
                    pagination
                    pageSizeOptions={pageSizeOptions}
                    rowHeight={ROW_HEIGHT}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    sx={{
                        borderRadius: 3,
                        bgcolor: '#fff',
                        fontSize: 13,
                        flex: 1,
                        boxShadow: 'none',
                        '& .MuiDataGrid-columnHeaders': {
                            bgcolor: '#FAFAFC',
                            fontWeight: 700,
                            fontSize: 13,
                            borderTopLeftRadius: 12,
                            borderTopRightRadius: 12,
                            '& .MuiDataGrid-columnHeaderTitle': {
                                fontWeight: 700,
                            }
                        },
                        '& .MuiDataGrid-cell': { fontSize: 12, py: 0, color: '#000' },
                        '& .MuiDataGrid-row': { minHeight: 32, maxHeight: 32 },
                        '& .MuiDataGrid-virtualScroller': { overflowX: 'auto !important' },
                        '& .MuiTablePagination-root': { fontSize: 12, borderTop: '1px solid #e0e0e0' },
                        '& .MuiDataGrid-footerContainer': {
                            bgcolor: '#FAFAFC',
                            borderBottomLeftRadius: 12,
                            borderBottomRightRadius: 12,
                        },
                        '& .MuiDataGrid-selectedRowCount': { color: '#23244A' },
                        '& .MuiDataGrid-cell[data-field="actions"]': {
                            position: 'sticky',
                            right: 0,
                            backgroundColor: '#fff',
                            zIndex: 1,
                        },

                        '& .MuiDataGrid-columnHeader[data-field="actions"]': {
                            position: 'sticky',
                            right: 0,
                            backgroundColor: '#FAFAFC',
                            zIndex: 2,
                        },
                    }}
                />
            </Box>
            <Dialog open={open} onClose={handleOnClose} maxWidth="sm" fullWidth>
                <AddOrders
                    open={open}
                    onClose={handleOnClose}
                    tagList={tagList}
                    initialvalues={editRow}
                    crmTags={crmTags}
                    customFieldList={customFieldList}
                />
            </Dialog>
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        background: '#FAFAFC',
                        boxShadow: '0 2px 16px rgba(24,26,42,0.10)',
                        minWidth: 320,
                    }
                }}
            >
                <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
                    Confirm Delete
                </DialogTitle>
                <DialogContent sx={{ fontSize: 15, maxHeight: "80vh", overflowY: "auto" }}>
                    Are you sure you want to delete this Open Orders mapping?
                </DialogContent>
                <DialogActions sx={{ py: 2, px: 3 }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} color="secondary" variant="outlined" size="small"
                        sx={{
                            borderRadius: 2,
                            border: 'none',
                            background: '#dedede',
                            color: '#000',
                            textTransform: 'capitalize',
                            py: 1,
                            px: 2,
                            '&:hover': { background: '#d6d6d6' },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained" size="small"
                        sx={{
                            borderRadius: 2,
                            background: '#d32f2f',
                            textTransform: 'capitalize',
                            py: 1,
                            px: 2,
                            '&:hover': { background: '#b71c1c' }
                        }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default OpenOrderList;